/* eslint-disable indent */
import { colors } from '@globals/themes/defaultTheme'
import { mobileQuery } from '@globals/themes/themeConstants'
import { useCurrentMember } from '@globals/useCurrentMember'
import { InitiateUploadParams, MediaObjectUpload } from '@kudos/http-client'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import { SxProps } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import HiddenFileInput from '@shared/components/mediaUploads/HiddenFileInput'
import GiphySearchDialog from '@tenant/recognition/RecognitionModal/sendRecognitionView/giphySearch/GiphySearchDialog'
import RefineMessageButton from '@tenant/recognition/RecognitionModal/sendRecognitionView/RefineMessageButton'
import { useT } from '@transifex/react'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
// Required JS files.
import 'froala-editor/js/froala_editor.pkgd.min.js'
import 'froala-editor/js/plugins.pkgd.min.js'
import 'froala-editor/js/plugins/char_counter.min.js'
import 'froala-editor/js/plugins/emoticons.min.js'
import 'froala-editor/js/plugins/image.min.js'
import 'froala-editor/js/plugins/video.min.js'
// Required CSS files
import 'froala-editor/css/froala_editor.pkgd.min.css'
import 'froala-editor/css/froala_style.min.css'
import 'froala-editor/css/plugins/char_counter.min.css'
import 'froala-editor/css/plugins/emoticons.min.css'
import 'froala-editor/css/plugins/image.min.css'
import 'froala-editor/css/plugins/video.min.css'
import { useRef } from 'react'
import { Control, Controller, FieldValues, Path, useFormContext } from 'react-hook-form'
import ErrorText from '../ErrorText'
import FieldLabel from '../FieldLabel'
import { getRichTextLength } from './getRichTextLength'
import { getRichTextEditorFieldStyles } from './richTextEditorFieldStyles'
import useEditorButtons from './useEditorButtons'
import useFroalaAttachments from './useFroalaAttachments'
import useFroalaGifs from './useFroalaGifs'
import { useFroalaMentions } from './useFroalaMentions'
import useRichTextImageUpload from './useRichTextImageUpload'
// Tribute.js CSS
import 'tributejs/dist/tribute.css'
import HelpText from '../HelpText'

const FroalaEditor = lazy(() => import('react-froala-wysiwyg'))

export type RichTextEditor = {
  image: {
    insert: (url: string, text: string | null, convert: boolean | null, figure: null) => void
    get: () => null
  }
  html?: {
    get: () => string
    set: (html: string) => void
  }
  edit: {
    off: () => void
    on: () => void
  }
  $el: HTMLElement & { [key: string]: HTMLElement }
}

export type RichTextEditorFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  label?: React.ReactNode
  minHeight?: number
  help?: React.ReactNode
  placeholder?: string
  required?: boolean
  disabled?: boolean
  labelStyle?: SxProps
  imageUploadFn?: (params: InitiateUploadParams, isInline?: boolean) => Promise<MediaObjectUpload>
  areGifsSupported?: boolean
  areMentionsSupported?: boolean
  attachmentUploadFn?: (params: {
    originalFilename: string
    contentType: string
  }) => Promise<MediaObjectUpload>
  attachmentFieldName?: string
  gifFieldName?: string
  gifModalId?: string
  isFullVersion?: boolean
  charCounterMax: number
  autoFocus?: boolean
  canRefineMessage?: boolean
  /**
   * When true, the editor starts compact (small height, no toolbar).
   * On first focus/click it expands to full height and shows the toolbar.
   */
  compactUntilFocus?: boolean
  /**
   * Called when the editor expands or collapses (only relevant when compactUntilFocus is true).
   */
  onExpandedChange?: (expanded: boolean) => void
  /**
   * Called when the user presses Enter with the editor's current HTML (so the form can use it
   * without relying on sync with form state). When compactUntilFocus is true, this is still
   * called and the editor is also collapsed.
   */
  onEnterSubmit?: (currentValue: string) => void
  /**
   * Used for conditional styling in compact mode
   */
  hasTextContent?: boolean
}

const RichTextEditorField = <T extends FieldValues>({
  autoFocus = false,
  control,
  label = null,
  help = null,
  name,
  minHeight = 150,
  placeholder,
  required = true,
  disabled,
  labelStyle,
  imageUploadFn,
  areGifsSupported = false,
  areMentionsSupported = false,
  attachmentUploadFn,
  attachmentFieldName = 'attachments',
  gifFieldName = 'gif',
  gifModalId = 'gifModal',
  isFullVersion = true,
  charCounterMax,
  canRefineMessage = false,
  compactUntilFocus = false,
  onExpandedChange,
  onEnterSubmit,
  hasTextContent,
}: RichTextEditorFieldProps<T>) => {
  const t = useT()
  const [isExpanded, setIsExpanded] = useState(!compactUntilFocus)
  const { upload } = useRichTextImageUpload(imageUploadFn)
  const { member: tenantMember } = useCurrentMember()
  const isMobile = useMediaQuery(mobileQuery)
  const editorRef = useRef<RichTextEditor | null>(null)
  const editorContainerRef = useRef<HTMLDivElement | null>(null)
  const [editorInstanceForMentions, setEditorInstanceForMentions] = useState<RichTextEditor | null>(
    null,
  )
  const onChangeRef = useRef<((html: string) => void) | null>(null)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isCompact = compactUntilFocus && !isExpanded

  const { setError, clearErrors } = useFormContext()
  const setErrorRef = useRef(setError)
  const clearErrorsRef = useRef(clearErrors)
  setErrorRef.current = setError
  clearErrorsRef.current = clearErrors

  // Clear pending blur timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    }
  }, [])

  // When expanded with compactUntilFocus, close on click outside the editor.
  // Clicks on the mentions popup (.tribute-container) are not considered "outside" so the editor stays expanded.
  useEffect(() => {
    if (!compactUntilFocus || !isExpanded) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isOutsideEditor =
        editorContainerRef.current && !editorContainerRef.current.contains(target)
      const isOnMentionsPopup = (target as Element).closest?.('.tribute-container')
      if (isOutsideEditor && !isOnMentionsPopup) {
        setIsExpanded(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [compactUntilFocus, isExpanded])

  // Notify parent when collapsed (immediate). "Expanded" is notified from initialized() once the editor is ready to avoid button flicker during expand.
  useEffect(() => {
    if (!isExpanded) {
      onExpandedChange?.(false)
    }
  }, [isExpanded, onExpandedChange])
  const {
    moreTextButtons,
    moreParagraphButtons,
    moreRichButtons,
    moreMiscButtons,
    pluginsEnabled,
  } = useEditorButtons(
    isFullVersion,
    attachmentUploadFn,
    areGifsSupported,
    isCompact,
    imageUploadFn,
  )

  // Use GIF hook for all GIF-related functionality
  const { giphyDialogId, handleGifSelected, registerGifButtonCommand } = useFroalaGifs({
    fieldName: gifFieldName,
    modalId: gifModalId,
  })

  // Use attachment hook if attachmentUploadFn is provided
  // Note: This hook requires FormProvider context, so it will only work in forms
  // IMPORTANT: Pass the exact same control instance that AttachmentsField uses
  // Don't cast it - pass it as-is and let the hook handle the typing
  const controlForAttachments = control as unknown as Control<FieldValues>

  const attachmentHook = useFroalaAttachments(
    attachmentUploadFn
      ? {
          mutationFn: attachmentUploadFn,
          fieldName: attachmentFieldName,
          control: controlForAttachments,
        }
      : undefined,
  )

  // Initialize mentions if enabled (use state so hook runs after editor initializes; ref alone doesn't trigger re-render)
  useFroalaMentions(editorInstanceForMentions, tenantMember?.id, areMentionsSupported)

  const visibleButtons = () => {
    const buttons = isMobile
      ? moreTextButtons.filter((btn: string) => btn !== 'emoticons')
      : moreTextButtons

    return !!onEnterSubmit
      ? buttons.filter((btn: string) => btn !== 'formatOLSimple' && btn !== 'formatUL')
      : buttons
  }

  const editorConfig = useMemo(() => {
    registerGifButtonCommand()
    attachmentHook.registerAttachmentButtonCommand()

    return {
      imageAllowedTypes: ['jpeg', 'jpg', 'png', 'webp'],
      imageMaxSize: 10 * 1024 * 1024,
      placeholderText: placeholder,
      attribution: false,
      emoticonsUseImage: false,
      charCounterCount: false,
      autoFocus,
      toolbarSticky: false,
      toolbarButtons: {
        moreText: {
          buttons: visibleButtons(),
          buttonsVisible: isMobile ? 4 : 7,
        },
        moreParagraph: {
          buttons: moreParagraphButtons,
        },
        moreRich: {
          buttons: moreRichButtons,
        },
        moreMisc: {
          buttons: moreMiscButtons,
          align: 'right',
          buttonsVisible: 2,
        },
      },
      pluginsEnabled: pluginsEnabled,
      heightMin: isCompact ? 56 : minHeight,
      events: {
        'paste.after': function () {
          const editor = this as unknown as RichTextEditor
          setTimeout(() => {
            const current = editor?.html?.get?.() ?? ''
            onChangeRef.current?.(current)
          }, 100)
        },
        contentChanged() {
          const editor = this as unknown as RichTextEditor
          const currentHtml = editor?.html?.get?.() ?? ''
          const length = getRichTextLength(currentHtml)
          if (length > charCounterMax) {
            setErrorRef.current?.(name, {
              type: 'maxLength',
              message: t('Maximum {max} characters allowed', { max: charCounterMax }),
            })
          } else {
            clearErrorsRef.current?.(name)
          }
        },
        'image.beforeUpload': function (images: File[]) {
          const editor = this as unknown as RichTextEditor
          if (images.length && imageUploadFn) {
            const img = images[0]
            upload(img, editor, name)
            return false
          }
          // If imageUploadFn is not provided, allow default image upload behavior
          return true
        },
        ...(compactUntilFocus && {
          click: () => {
            if (!isExpanded) {
              setIsExpanded(true)
            } else if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current)
              blurTimeoutRef.current = null
            }
          },
        }),
        ...(compactUntilFocus &&
          isExpanded && {
            blur: () => {
              blurTimeoutRef.current = setTimeout(() => {
                const editorInstance = editorRef.current as unknown as {
                  el?: HTMLElement
                }
                const activeEl = document.activeElement
                // Use the full editor box (.fr-box) so toolbar buttons/dropdowns (e.g. formatUL)
                // don't trigger collapse when focus moves from the editable area to the toolbar
                const editorBox = editorInstance?.el?.closest('.fr-box')
                const focusInEditor = editorBox?.contains(activeEl)
                // Keep expanded when focus moved to the mentions popup (Tribute.js)
                const focusInMentionsPopup = activeEl?.closest?.('.tribute-container')
                if (editorBox && !focusInEditor && !focusInMentionsPopup) {
                  setIsExpanded(false)
                }
                blurTimeoutRef.current = null
              }, 150)
            },
          }),
        keydown(keydownEvent: KeyboardEvent & { originalEvent?: KeyboardEvent }) {
          const editor = this as unknown as RichTextEditor

          if (keydownEvent.key !== 'Enter') return
          if (!onEnterSubmit) return
          // Shift+Enter: allow default so the editor inserts a new line
          if (keydownEvent.shiftKey) return

          const nativeEvent = keydownEvent.originalEvent ?? keydownEvent
          nativeEvent.preventDefault()
          nativeEvent.stopPropagation()
          if (keydownEvent !== nativeEvent) {
            keydownEvent.preventDefault()
            keydownEvent.stopPropagation()
          }

          let currentValue = editor?.html?.get?.() ?? ''
          // Strip trailing empty block Froala may have inserted before we could prevent it
          currentValue = currentValue.replace(/\s*<p>(<br\s*\/?>)?\s*<\/p>\s*$/i, '')
          onEnterSubmit(currentValue)
          if (compactUntilFocus && isExpanded) setIsExpanded(false)
          return false
        },
        initialized() {
          const editor = this as unknown as RichTextEditor
          editorRef.current = editor
          setEditorInstanceForMentions(editor)

          const initialHtml = editor?.html?.get?.() ?? ''
          if (getRichTextLength(initialHtml) > charCounterMax) {
            setErrorRef.current?.(name, {
              type: 'maxLength',
              message: t('Maximum {max} characters allowed', { max: charCounterMax }),
            })
          }

          // Ensure buttons are registered when editor initializes
          if (areGifsSupported) {
            registerGifButtonCommand()
          }
          if (attachmentUploadFn) {
            attachmentHook.registerAttachmentButtonCommand()
          }

          if (disabled) {
            editor.edit?.off()
          } else {
            editor.edit?.on()
          }

          // Handle autofocus after editor is fully initialized
          const shouldFocus = autoFocus || (compactUntilFocus && isExpanded)
          if (shouldFocus) {
            // Use setTimeout to ensure focus happens after editor is fully rendered
            setTimeout(() => {
              const editorInstance = editorRef.current as unknown as {
                el?: HTMLElement
              }
              // Focus the editor element directly (editor.el is the Froala editor DOM element)
              if (editorInstance?.el) {
                editorInstance.el.focus()
              }
            }, 0)
          }
          // Notify parent only when the expanded editor has finished initializing (avoids button flicker during expand)
          if (compactUntilFocus && isExpanded) {
            onExpandedChange?.(true)
          }
        },
      },
    }
  }, [
    placeholder,
    disabled,
    areMentionsSupported,
    tenantMember?.id,
    name,
    upload,
    attachmentHook,
    imageUploadFn,
    registerGifButtonCommand,
    moreTextButtons,
    moreParagraphButtons,
    moreRichButtons,
    moreMiscButtons,
    pluginsEnabled,
    autoFocus,
    charCounterMax,
    isFullVersion,
    compactUntilFocus,
    isExpanded,
    isCompact,
    onExpandedChange,
    onEnterSubmit,
  ])

  return (
    <Box>
      <style>{getRichTextEditorFieldStyles(colors)}</style>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value }, fieldState: { error, isDirty } }) => {
          // Store onChange in ref so useFroalaGifs can access it
          onChangeRef.current = onChange
          const showError = Boolean(error) && (!isCompact || isDirty)
          const currentLength = getRichTextLength(value || '')
          const isOverLimit = currentLength > charCounterMax
          return (
            <>
              {label && (
                <FieldLabel
                  htmlFor={name}
                  required={required}
                  disabled={disabled}
                  labelStyle={labelStyle}>
                  {label}
                </FieldLabel>
              )}
              {help && <HelpText>{help}</HelpText>}
              <Box
                ref={editorContainerRef}
                sx={{ position: 'relative' }}
                {...(isCompact && { 'data-rich-text-compact': 'true' })}
                {...(isCompact &&
                  hasTextContent && { 'data-rich-text-compact-with-content': 'true' })}>
                <Suspense fallback={<Skeleton variant="rectangular" height={150} width="100%" />}>
                  <FroalaEditor
                    key={compactUntilFocus && !isExpanded ? 'compact' : 'expanded'}
                    tag="textarea"
                    config={editorConfig}
                    model={value || ''}
                    onModelChange={onChange}
                  />
                </Suspense>
                {!isCompact && (
                  <Typography
                    component="div"
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      position: 'absolute',
                      bottom: 8,
                      right: 16,
                      color: isOverLimit ? 'error.main' : 'text.secondary',
                    }}>
                    {currentLength} / {charCounterMax}
                  </Typography>
                )}
                {canRefineMessage && (
                  <Box sx={{ position: 'absolute', bottom: 8, left: 16, zIndex: 2 }}>
                    <RefineMessageButton />
                  </Box>
                )}
              </Box>

              <GiphySearchDialog onGifSelected={handleGifSelected} id={giphyDialogId} />

              {attachmentHook && (
                <HiddenFileInput
                  ref={attachmentHook.inputFileRef}
                  type="file"
                  accept="image/jpg, .png, .webp, .pdf, .docx, .pptx, .gif"
                  id="fileUploadAttachment"
                  onChange={attachmentHook.handleFileInputChange}
                />
              )}
              {attachmentHook?.maxSizeError && (
                <ErrorText>
                  {t('The file exceeds the maximum size limit, upload a smaller file')}
                </ErrorText>
              )}
              {error && (
                <Box mt={1.5} sx={{ minHeight: 22, display: showError ? 'block' : 'none' }}>
                  <ErrorText>{error.message}</ErrorText>
                </Box>
              )}
            </>
          )
        }}
      />
    </Box>
  )
}

export default RichTextEditorField
